export interface FormatNumberOptions {
  /** Fixed decimal places. Default 0. */
  decimals?: number;
  /** Trim trailing zeros from the fractional part. Default false. */
  trimTrailingZeros?: boolean;
}

const COMPACT_TIERS = [
  { divisor: 1_000_000_000, suffix: "B" },
  { divisor: 1_000_000, suffix: "M" },
  { divisor: 1_000, suffix: "K" },
] as const;

function groupIntegerDigits(integer: string): string {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function trimZeros(fractional: string): string {
  return fractional.replace(/0+$/, "");
}

function toFixedString(value: number, decimals: number, trimTrailingZeros: boolean): string {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const [intPart, decPart = ""] = abs.toFixed(decimals).split(".");
  const grouped = groupIntegerDigits(intPart!);
  if (!decPart) return sign + grouped;
  const decFinal = trimTrailingZeros ? trimZeros(decPart) : decPart;
  return decFinal ? `${sign}${grouped}.${decFinal}` : sign + grouped;
}

/**
 * Format a number with comma thousand separators. Pure — no `Intl`, no
 * `toLocale*`. Always en-US grouping (`1,234.56`).
 *
 * @example
 * formatNumberWithCommas(1234)                                      // "1,234"
 * formatNumberWithCommas(1234.5, { decimals: 2 })                   // "1,234.50"
 * formatNumberWithCommas(1234.5, { decimals: 2, trimTrailingZeros: true }) // "1,234.5"
 */
export function formatNumberWithCommas(value: number, options?: FormatNumberOptions): string {
  if (!Number.isFinite(value)) return "0";
  return toFixedString(value, options?.decimals ?? 0, options?.trimTrailingZeros ?? false);
}

/**
 * Format a number in compact notation (`1K` / `1.2M` / `45.3B`). Pure — no
 * `Intl`. Trims trailing zeros by default, so `1000` renders as `"1K"`, not
 * `"1.0K"` (matches the user's mental model and previous `Intl` output).
 *
 * @example
 * formatCompactNumber(1000)                                  // "1K"
 * formatCompactNumber(1234, { decimals: 1 })                 // "1.2K"
 * formatCompactNumber(1234567, { decimals: 2 })              // "1.23M"
 * formatCompactNumber(999)                                   // "999"
 */
export function formatCompactNumber(value: number, options?: FormatNumberOptions): string {
  if (!Number.isFinite(value)) return "0";
  const decimals = options?.decimals ?? 1;
  const trim = options?.trimTrailingZeros ?? true;
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);

  for (const { divisor, suffix } of COMPACT_TIERS) {
    if (abs >= divisor) {
      return `${sign}${toFixedString(abs / divisor, decimals, trim)}${suffix}`;
    }
  }
  return sign + toFixedString(abs, decimals, trim);
}

/**
 * Format a percentage value as `"42%"` or `"12.5%"`. Pure — no `Intl`. The
 * `%` is part of the output; callers must not append another one.
 *
 * @example
 * formatPercent(42)        // "42%"
 * formatPercent(12.345, 1) // "12.3%"
 */
export function formatPercent(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return "0%";
  return `${toFixedString(value, decimals, false)}%`;
}
